var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import isEqual from "lodash/isEqual";
import { __, } from "@jti/fe-common";
import { CasCustomAttrsApi } from "../services/cas/CasCustomAttrsApi";
import { CasPurposesApi } from "../services/cas/CasPurposesApi";
export const useOtPurposesByUser = (step) => {
    const getPurposesForUserByStep = () => __awaiter(void 0, void 0, void 0, function* () {
        const data = yield CasPurposesApi.init().getPurposesForUserByStep(step);
        if (!data.length) {
            throw new Error("Failed to fetch from OT");
        }
        return data;
    });
    return useQuery("getPurposesByUser", getPurposesForUserByStep);
};
export const useOtPurposesForUserByCategory = (category) => {
    const getPurposesForUserByStep = () => __awaiter(void 0, void 0, void 0, function* () {
        const data = yield CasPurposesApi.init().getPurposesForUserByCategory(category);
        if (!data.length) {
            throw new Error("Failed to fetch from OT");
        }
        return data;
    });
    return useQuery("getPurposesByUser", getPurposesForUserByStep);
};
export const useOtPurposesByStep = (step, isLast = true) => {
    const getPurposesByStep = () => __awaiter(void 0, void 0, void 0, function* () {
        const data = yield CasPurposesApi.init().getPurposesByStep(step);
        if (!data.length) {
            throw new Error("Failed to fetch from OT");
        }
        return data;
    });
    return useQuery(["getPurposesByStep", step], getPurposesByStep, { enabled: isLast });
};
const groupPurposeByCategory = (purposes, purposesDescriptions) => {
    const groupedPurposes = purposes.reduce((acc, currentPurpose) => {
        const matchedCategoryPurpose = acc.find((category) => category.category === currentPurpose.category);
        const preparedPurposeData = Object.assign(Object.assign({}, currentPurpose), { purposeName: currentPurpose.description, purposeDescription: __(`profile.communication.manage_consent.${currentPurpose.category}.label`) });
        if (matchedCategoryPurpose) {
            matchedCategoryPurpose.purposes.push(preparedPurposeData);
        }
        else {
            acc.push({
                purposeTitle: __(`profile.communication.manage_consent.${currentPurpose.category}.title`),
                purposeDescription: purposesDescriptions === null || purposesDescriptions === void 0 ? void 0 : purposesDescriptions[currentPurpose.category],
                purposeId: currentPurpose.name,
                category: currentPurpose.category,
                purposes: [preparedPurposeData],
            });
        }
        return acc;
    }, []);
    const orderOfGroups = ["email", "sms", "post", "survey", "additional", "GDPR"];
    return groupedPurposes.sort((compareCategoryFirst, compareCategorySecond) => orderOfGroups.indexOf(compareCategoryFirst.category) - orderOfGroups.indexOf(compareCategorySecond.category));
};
const groupPurposesBySection = (purposes) => {
    const sectionGroupedPurposes = purposes.reduce((acc, currentPurpose) => {
        const sectionPurpose = acc.find((purpose) => purpose.purposeId === currentPurpose.section.id);
        const preparedPurposeData = Object.assign(Object.assign({}, currentPurpose), { purposeName: currentPurpose.name, purposeDescription: currentPurpose.description });
        if (sectionPurpose) {
            sectionPurpose.purposes.push(preparedPurposeData);
        }
        else {
            acc.push({
                purposeTitle: currentPurpose.section.name,
                purposeDescription: currentPurpose.section.description,
                purposeId: currentPurpose.section.id,
                category: currentPurpose.category,
                purposes: [preparedPurposeData],
            });
        }
        return acc;
    }, []);
    return sectionGroupedPurposes;
};
const purposesList = {
    category: groupPurposeByCategory,
    section: groupPurposesBySection,
};
export const useOtPurposes = (step, groupingType = "category", purposesDescriptions) => {
    const updatePurposes = useMutation("purposes", ({ consents, preferredLanguage }) => CasCustomAttrsApi.init().writeConsents({
        consents,
        preferredLanguage,
    }));
    const update = (dataToUpdate) => __awaiter(void 0, void 0, void 0, function* () {
        yield updatePurposes.mutateAsync(dataToUpdate);
    });
    const { data: purposesByStep, isLoading: isLoadingSteps, isFetching: isFetchingSteps, isError: isErrorSteps, } = useOtPurposesByStep(step);
    const { data: purposesByUser, isLoading: isLoadingUser, isFetching: isFetchingUserData, isError: isErrorUser, } = useOtPurposesForUserByCategory("all");
    const [purposes, setPurposes] = useState([]);
    const [groupedPurposes, setGroupedPurposes] = useState([]);
    const prevGroupedPurposesRef = useRef([]);
    useEffect(() => {
        if (purposesByStep && purposesByUser) {
            const filteredPurposes = purposesByStep.reduce((purposesAcc, purposeCurr) => {
                const userPurpose = purposesByUser.find((purposeByUser) => purposeByUser.id === purposeCurr.id);
                if (userPurpose) {
                    purposesAcc.push(Object.assign(Object.assign({}, userPurpose), { mandatory: purposeCurr.mandatory, section: purposeCurr.section }));
                }
                return purposesAcc;
            }, []);
            setPurposes(filteredPurposes);
        }
    }, [isLoadingSteps, isLoadingUser, purposesByStep, purposesByUser]);
    useEffect(() => {
        if (purposes.length > 0) {
            const groupedConsents = purposesList[groupingType](purposes, purposesDescriptions);
            if (!isEqual(groupedConsents, prevGroupedPurposesRef.current)) {
                setGroupedPurposes(groupedConsents);
                prevGroupedPurposesRef.current = groupedConsents;
            }
        }
    }, [purposes, groupingType, purposesDescriptions]);
    return {
        purposes,
        groupedPurposes,
        isLoading: isLoadingSteps || isLoadingUser,
        isFetching: isFetchingSteps || isFetchingUserData,
        isUpdating: updatePurposes.isLoading,
        isError: isErrorSteps || isErrorUser,
        update,
    };
};
//# sourceMappingURL=useOtPurposes.js.map